'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import RoleSelector from './RoleSelector';
import QuestionCard from './QuestionCard';
import AssessmentProgress from './AssessmentProgress';
import SectionCard from './SectionCard';
import ResultsCard from './ResultsCard';
import BenchmarkComparison from './BenchmarkComparison';
import ContextualRecommendations from '@/app/ai-readiness-assessment/components/ContextualRecommendations';
import { trackAssessmentStart, trackAssessmentSectionComplete, trackAssessmentComplete } from '@/lib/analytics';


interface Role {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface Option {
  id: string;
  label: string;
  value: number;
  description?: string;
}

interface Question {
  id: string;
  question: string;
  description?: string;
  options: Option[];
}

interface Section {
  id: string;
  title: string;
  description: string;
  icon: string;
  questions: Question[];
}

interface AssessmentData {
  [sectionId: string]: {
    [questionId: string]: string;
  };
}

const AssessmentInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [assessmentData, setAssessmentData] = useState<AssessmentData>({});
  const [showResults, setShowResults] = useState(false);
  const [completedSections, setCompletedSections] = useState<string[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const roles: Role[] = [
    {
      id: 'executive',
      title: 'Executive Leadership',
      description: 'C-suite and senior executives focused on strategic AI adoption and business outcomes',
      icon: 'BriefcaseIcon'
    },
    {
      id: 'it-leader',
      title: 'IT Leadership',
      description: 'CTOs and IT directors responsible for technical implementation and infrastructure',
      icon: 'ServerIcon'
    },
    {
      id: 'compliance',
      title: 'Compliance Officer',
      description: 'Risk and compliance professionals ensuring regulatory adherence',
      icon: 'ShieldCheckIcon'
    }
  ];

  const sections: Section[] = [
    {
      id: 'organizational',
      title: 'Organizational Readiness',
      description: 'Assess your organization\'s strategic alignment and cultural preparedness for AI adoption',
      icon: 'BuildingOfficeIcon',
      questions: [
        {
          id: 'q1',
          question: 'How would you describe your organization\'s current AI strategy?',
          description: 'Understanding your strategic foundation is crucial for successful AI implementation',
          options: [
            { id: 'a', label: 'Comprehensive AI strategy with clear roadmap', value: 5, description: 'Documented strategy with executive buy-in and defined milestones' },
            { id: 'b', label: 'Initial strategy discussions underway', value: 3, description: 'Exploring AI opportunities with preliminary planning' },
            { id: 'c', label: 'Ad-hoc AI initiatives without formal strategy', value: 2, description: 'Tactical AI projects without strategic coordination' },
            { id: 'd', label: 'No formal AI strategy in place', value: 1, description: 'AI adoption not yet prioritized at strategic level' }
          ]
        },
        {
          id: 'q2',
          question: 'What level of executive sponsorship exists for AI initiatives?',
          description: 'Executive support is critical for overcoming organizational barriers',
          options: [
            { id: 'a', label: 'Active C-suite champion with dedicated resources', value: 5 },
            { id: 'b', label: 'General executive support without dedicated ownership', value: 3 },
            { id: 'c', label: 'Limited executive awareness or interest', value: 2 },
            { id: 'd', label: 'No executive engagement on AI topics', value: 1 }
          ]
        },
        {
          id: 'q3',
          question: 'How would you characterize your organization\'s change management capabilities?',
          options: [
            { id: 'a', label: 'Proven track record with structured change processes', value: 5 },
            { id: 'b', label: 'Moderate capability with some successful changes', value: 3 },
            { id: 'c', label: 'Limited experience with organizational change', value: 2 },
            { id: 'd', label: 'Significant resistance to change initiatives', value: 1 }
          ]
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Infrastructure',
      description: 'Evaluate your current technology stack and data readiness for AI integration',
      icon: 'CpuChipIcon',
      questions: [
        {
          id: 'q4',
          question: 'What is the current state of your data infrastructure?',
          description: 'Quality data infrastructure is foundational for AI success',
          options: [
            { id: 'a', label: 'Modern data platform with clean, accessible data', value: 5, description: 'Cloud-based infrastructure with data governance in place' },
            { id: 'b', label: 'Functional systems with some data quality issues', value: 3, description: 'Legacy systems with ongoing modernization efforts' },
            { id: 'c', label: 'Fragmented systems with significant data silos', value: 2, description: 'Multiple disconnected systems requiring integration' },
            { id: 'd', label: 'Limited data infrastructure or accessibility', value: 1, description: 'Manual processes with minimal digital data capture' }
          ]
        },
        {
          id: 'q5',
          question: 'How mature is your organization\'s API and integration architecture?',
          options: [
            { id: 'a', label: 'Comprehensive API strategy with modern integration layer', value: 5 },
            { id: 'b', label: 'Basic APIs with point-to-point integrations', value: 3 },
            { id: 'c', label: 'Limited integration capabilities', value: 2 },
            { id: 'd', label: 'Minimal or no API infrastructure', value: 1 }
          ]
        },
        {
          id: 'q6',
          question: 'What level of cloud infrastructure does your organization utilize?',
          options: [
            { id: 'a', label: 'Cloud-first with modern DevOps practices', value: 5 },
            { id: 'b', label: 'Hybrid cloud with ongoing migration', value: 3 },
            { id: 'c', label: 'Primarily on-premise with limited cloud adoption', value: 2 },
            { id: 'd', label: 'Entirely on-premise infrastructure', value: 1 }
          ]
        }
      ]
    },
    {
      id: 'governance',
      title: 'Governance & Compliance',
      description: 'Review your governance frameworks and compliance readiness for AI deployment',
      icon: 'ShieldCheckIcon',
      questions: [
        {
          id: 'q7',
          question: 'Does your organization have established AI governance policies?',
          description: 'Governance frameworks ensure responsible and compliant AI usage',
          options: [
            { id: 'a', label: 'Comprehensive AI governance framework in place', value: 5, description: 'Documented policies with oversight committee and review processes' },
            { id: 'b', label: 'Basic policies under development', value: 3, description: 'Initial governance discussions with draft policies' },
            { id: 'c', label: 'General IT governance without AI-specific policies', value: 2, description: 'Existing governance not adapted for AI considerations' },
            { id: 'd', label: 'No formal governance framework', value: 1, description: 'Ad-hoc decision making without structured oversight' }
          ]
        },
        {
          id: 'q8',
          question: 'How well does your organization understand relevant AI regulations?',
          description: 'Regulatory compliance varies by jurisdiction and industry',
          options: [
            { id: 'a', label: 'Comprehensive understanding with compliance program', value: 5 },
            { id: 'b', label: 'General awareness with ongoing education', value: 3 },
            { id: 'c', label: 'Limited knowledge of AI-specific regulations', value: 2 },
            { id: 'd', label: 'Minimal regulatory awareness', value: 1 }
          ]
        },
        {
          id: 'q9',
          question: 'What data privacy and security measures are currently in place?',
          options: [
            { id: 'a', label: 'Enterprise-grade security with regular audits', value: 5 },
            { id: 'b', label: 'Standard security practices with some gaps', value: 3 },
            { id: 'c', label: 'Basic security with significant vulnerabilities', value: 2 },
            { id: 'd', label: 'Minimal security infrastructure', value: 1 }
          ]
        }
      ]
    },
    {
      id: 'talent',
      title: 'Talent & Skills',
      description: 'Assess your team\'s AI capabilities and training readiness',
      icon: 'AcademicCapIcon',
      questions: [
        {
          id: 'q10',
          question: 'What AI expertise exists within your organization?',
          description: 'Internal capability accelerates AI adoption and reduces dependency',
          options: [
            { id: 'a', label: 'Dedicated AI team with proven expertise', value: 5, description: 'Data scientists and ML engineers with successful projects' },
            { id: 'b', label: 'Some technical staff with AI interest or training', value: 3, description: 'Developers exploring AI without specialized expertise' },
            { id: 'c', label: 'Limited technical knowledge of AI concepts', value: 2, description: 'General IT team without AI-specific skills' },
            { id: 'd', label: 'No internal AI expertise', value: 1, description: 'Complete reliance on external resources for AI knowledge' }
          ]
        },
        {
          id: 'q11',
          question: 'How prepared is your workforce for AI-augmented roles?',
          options: [
            { id: 'a', label: 'Active training programs with high engagement', value: 5 },
            { id: 'b', label: 'Some training initiatives underway', value: 3 },
            { id: 'c', label: 'Limited training with resistance to change', value: 2 },
            { id: 'd', label: 'No AI training or preparation programs', value: 1 }
          ]
        },
        {
          id: 'q12',
          question: 'What is your organization\'s approach to AI talent acquisition?',
          options: [
            { id: 'a', label: 'Strategic hiring plan with competitive positioning', value: 5 },
            { id: 'b', label: 'Opportunistic hiring as needs arise', value: 3 },
            { id: 'c', label: 'Difficulty attracting AI talent', value: 2 },
            { id: 'd', label: 'No active AI talent acquisition strategy', value: 1 }
          ]
        }
      ]
    }
  ];

  const handleRoleSelect = (roleId: string) => {
    if (!isHydrated) return;
    setSelectedRole(roleId);
    trackAssessmentStart(roleId);
  };

  const handleAnswerSelect = (optionId: string) => {
    if (!isHydrated) return;
    
    const section = sections[currentSection];
    const question = section.questions[currentQuestion];
    
    setAssessmentData(prev => ({
      ...prev,
      [section.id]: {
        ...prev[section.id],
        [question.id]: optionId
      }
    }));
  };

  const handleNext = () => {
    if (!isHydrated) return;
    
    const section = sections[currentSection];
    
    if (currentQuestion < section.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentSection < sections.length - 1) {
      if (!completedSections.includes(section.id)) {
        setCompletedSections([...completedSections, section.id]);
      }
      trackAssessmentSectionComplete(section.title, currentSection);
      setCurrentSection(currentSection + 1);
      setCurrentQuestion(0);
    } else {
      if (!completedSections.includes(section.id)) {
        setCompletedSections([...completedSections, section.id]);
      }
      // Track assessment completion before showing results
      const results = calculateResults();
      trackAssessmentComplete({
        role: selectedRole,
        score: results.overallScore,
        readinessLevel: results.readinessLevel,
      });
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (!isHydrated) return;
    
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      setCurrentQuestion(sections[currentSection - 1].questions.length - 1);
    }
  };

  const calculateResults = () => {
    let totalScore = 0;
    let maxScore = 0;
    const sectionScores: { [key: string]: { score: number; max: number } } = {};

    sections.forEach(section => {
      let sectionScore = 0;
      let sectionMax = 0;

      section.questions.forEach(question => {
        const answer = assessmentData[section.id]?.[question.id];
        if (answer) {
          const option = question.options.find(opt => opt.id === answer);
          if (option) {
            sectionScore += option.value;
          }
        }
        sectionMax += 5;
      });

      sectionScores[section.id] = { score: sectionScore, max: sectionMax };
      totalScore += sectionScore;
      maxScore += sectionMax;
    });

    const overallPercentage = Math.round((totalScore / maxScore) * 100);
    
    let readinessLevel = '';
    if (overallPercentage >= 80) readinessLevel = 'Highly Ready for AI Adoption';
    else if (overallPercentage >= 60) readinessLevel = 'Ready with Some Preparation Needed';
    else if (overallPercentage >= 40) readinessLevel = 'Moderately Ready - Significant Preparation Required';
    else readinessLevel = 'Early Stage - Foundation Building Needed';

    return {
      overallScore: overallPercentage,
      readinessLevel,
      sectionScores
    };
  };

  const getCurrentAnswer = () => {
    const section = sections[currentSection];
    const question = section.questions[currentQuestion];
    return assessmentData[section.id]?.[question.id] || '';
  };

  const isAnswered = () => {
    return getCurrentAnswer() !== '';
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-12 bg-muted rounded w-3/4"></div>
              <div className="h-6 bg-muted rounded w-1/2"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-48 bg-muted rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedRole) {
    return (
      <div className="space-y-12">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-secondary mb-4">
            Select Your Role
          </h2>
          <p className="text-lg text-muted-foreground">
            Choose the role that best describes your position to receive a customized assessment experience tailored to your responsibilities and concerns.
          </p>
        </div>

        <RoleSelector
          roles={roles}
          selectedRole={selectedRole}
          onSelectRole={handleRoleSelect}
        />

        <div className="bg-muted/50 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0" />
            <div>
              <h3 className="font-display font-semibold text-secondary mb-2">About This Assessment</h3>
              <p className="text-sm text-muted-foreground mb-2">
                This comprehensive assessment evaluates your organization across four critical dimensions: Organizational Readiness, Technical Infrastructure, Governance & Compliance, and Talent & Skills.
              </p>
              <p className="text-sm text-muted-foreground">
                The assessment takes approximately 10-15 minutes to complete and provides immediate, actionable insights along with industry benchmarking data.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const results = calculateResults();
    
    const metrics = sections.map(section => {
      const sectionData = results.sectionScores[section.id];
      const percentage = Math.round((sectionData.score / sectionData.max) * 100);
      
      let status: 'excellent' | 'good' | 'fair' | 'needs-improvement' = 'needs-improvement';
      if (percentage >= 80) status = 'excellent';
      else if (percentage >= 60) status = 'good';
      else if (percentage >= 40) status = 'fair';
      
      return {
        label: section.title,
        score: sectionData.score,
        maxScore: sectionData.max,
        status
      };
    });

    const benchmarks = [
      { category: 'Organizational Readiness', yourScore: metrics[0].status === 'excellent' ? 85 : metrics[0].status === 'good' ? 70 : metrics[0].status === 'fair' ? 55 : 35, industryAverage: 62, topPerformers: 88 },
      { category: 'Technical Infrastructure', yourScore: metrics[1].status === 'excellent' ? 82 : metrics[1].status === 'good' ? 68 : metrics[1].status === 'fair' ? 52 : 32, industryAverage: 58, topPerformers: 85 },
      { category: 'Governance & Compliance', yourScore: metrics[2].status === 'excellent' ? 88 : metrics[2].status === 'good' ? 72 : metrics[2].status === 'fair' ? 58 : 38, industryAverage: 65, topPerformers: 92 },
      { category: 'Talent & Skills', yourScore: metrics[3].status === 'excellent' ? 80 : metrics[3].status === 'good' ? 65 : metrics[3].status === 'fair' ? 50 : 30, industryAverage: 55, topPerformers: 82 }
    ];

    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-secondary mb-4">
            Your AI Readiness Results
          </h2>
          <p className="text-lg text-muted-foreground">
            Based on your responses, here's a comprehensive analysis of your organization's AI readiness
          </p>
        </div>

        <ResultsCard
          overallScore={results.overallScore}
          metrics={metrics}
          readinessLevel={results.readinessLevel}
          recommendations={[]}
        />

        <BenchmarkComparison benchmarks={benchmarks} />

        <ContextualRecommendations 
          assessmentData={assessmentData}
          sectionScores={results.sectionScores}
          selectedRole={selectedRole}
        />

        <div className="bg-gradient-to-br from-primary to-accent rounded-lg p-8 text-center">
          <h3 className="text-2xl font-display font-bold text-white mb-4">
            Ready to Take the Next Step?
          </h3>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Schedule a complimentary consultation with our AI governance experts to discuss your results and develop a customized implementation roadmap for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.location.href = '/partnership-inquiry'}
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-primary font-display font-semibold rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5"
            >
              Schedule Consultation
              <Icon name="ArrowRightIcon" size={20} className="ml-2" />
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center justify-center px-8 py-3 bg-white/10 text-white font-display font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-all duration-300"
            >
              <Icon name="DocumentArrowDownIcon" size={20} className="mr-2" />
              Download Report
            </button>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={() => {
              setShowResults(false);
              setSelectedRole('');
              setCurrentSection(0);
              setCurrentQuestion(0);
              setAssessmentData({});
              setCompletedSections([]);
            }}
            className="text-primary hover:text-primary/80 font-medium transition-colors duration-300"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    );
  }

  const section = sections[currentSection];
  const question = section.questions[currentQuestion];
  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);
  const currentQuestionNumber = sections.slice(0, currentSection).reduce((sum, s) => sum + s.questions.length, 0) + currentQuestion + 1;

  return (
    <div className="space-y-8">
      <AssessmentProgress
        currentStep={currentSection + 1}
        totalSteps={sections.length}
        completedSections={completedSections.length}
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <h3 className="text-lg font-display font-semibold text-secondary mb-4">Assessment Sections</h3>
          {sections.map((sec, index) => (
            <SectionCard
              key={sec.id}
              title={sec.title}
              description={sec.description}
              icon={sec.icon}
              isCompleted={completedSections.includes(sec.id)}
              isCurrent={index === currentSection}
              score={completedSections.includes(sec.id) ? 75 : undefined}
            />
          ))}
        </div>

        <div className="lg:col-span-3 space-y-6">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                Question {currentQuestionNumber} of {totalQuestions}
              </span>
              <span className="text-sm font-medium text-primary">
                {section.title}
              </span>
            </div>
          </div>

          <QuestionCard
            question={question.question}
            description={question.description}
            options={question.options}
            selectedValue={getCurrentAnswer()}
            onSelect={handleAnswerSelect}
            questionNumber={currentQuestionNumber}
          />

          <div className="flex items-center justify-between pt-4">
            <button
              onClick={handlePrevious}
              disabled={currentSection === 0 && currentQuestion === 0}
              className="inline-flex items-center px-6 py-3 text-primary font-display font-medium rounded-lg border-2 border-primary hover:bg-primary hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-primary"
            >
              <Icon name="ArrowLeftIcon" size={20} className="mr-2" />
              Previous
            </button>

            <button
              onClick={handleNext}
              disabled={!isAnswered()}
              className="inline-flex items-center px-8 py-3 bg-accent text-white font-display font-semibold rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:translate-y-0"
            >
              {currentSection === sections.length - 1 && currentQuestion === section.questions.length - 1 ? 'View Results' : 'Next'}
              <Icon name="ArrowRightIcon" size={20} className="ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentInteractive;